import { ChevronLeft, ChevronRight } from "lucide-react";

import { commafy } from "@/components/helpers/string-utils";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface PagingProps {
  totalRecords: number;
  pageSize: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  isSticky?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

export function Paging({
  totalRecords,
  pageSize,
  currentPage,
  setCurrentPage,
  isSticky = false,
  style,
  className,
}: PagingProps) {
  const maxPageNumber =
    Math.floor(totalRecords / pageSize) +
    (totalRecords % pageSize === 0 ? 0 : 1);
  const allowPrev = currentPage > 1;
  const allowNext = currentPage < maxPageNumber;
  const minRecordIndex =
    (currentPage - 1) * pageSize + (totalRecords > 0 ? 1 : 0);
  const overflowSize = !allowNext ? currentPage * pageSize - totalRecords : 0;
  const maxRecordIndex =
    totalRecords > 0 ? currentPage * pageSize - overflowSize : 0;

  const onPrev = () => {
    setCurrentPage(currentPage - 1);
  };

  const onNext = () => {
    setCurrentPage(currentPage + 1);
  };

  return (
    <div
      className={cn(
        "flex justify-center items-center",
        isSticky && "lg:sticky lg:bottom-0 lg:z-10",
        className,
      )}
      style={style}
    >
      <div className="flex min-w-44 justify-between items-center">
        <Button
          variant="outline"
          size="icon"
          disabled={!allowPrev}
          onClick={onPrev}
          className="p-0 rounded-md w-9 h-10"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="mx-4 text-sm">
          {minRecordIndex}-{maxRecordIndex} of {commafy(totalRecords, 0, 0)}
        </div>
        <Button
          variant="outline"
          size="icon"
          disabled={!allowNext}
          onClick={onNext}
          className="p-0 rounded-md w-9 h-10"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
